import { useState, useEffect, useRef } from 'react'

// Loads Double the Donation's plugin and captures the donor's employer for the
// order POST. Registration is server-side only (DoubleTheDonationRegistrationJob).

// Never DTD's own `dd-company-name-input`: their deferred auto-init scans for it
// and replaces our v2 instance, killing our callbacks.
export const CONTAINER_ID = 'pledge-employer-search'

// Separate because v7 can have the search and the results in the document at once.
export const RESULTS_CONTAINER_ID = 'pledge-employer-match-results'

// The v1 fallback finds its own target by id, so that branch hands it back.
const DTD_LEGACY_ID = 'dd-company-name-input'

// The share DoubleTheDonationRegistrationJob gates on.
export const registersEmployer = shares =>
  !!shares?.includes('extended_with_beneficiary')

// Only donations the server will register get attributed. `shares` is separate
// because v6's completed donation lacks it for wallet, crypto and DAF.
export const trackedIdentifier = (donation, shares = donation?.shares) =>
  registersEmployer(shares) ? donation?.uuid ?? null : null

// Is `text` still an edit of `name`? Symmetric prefix, since the plugin echoes
// text around a selection in no fixed order. Callers guard empty text.
const stillMatches = (name, text) => {
  if (!name) return false
  const selected = name.trim().toLowerCase()
  const typed = text.trim().toLowerCase()
  return selected.startsWith(typed) || typed.startsWith(selected)
}

const useEmployerMatching = config => {
  const enabled = !!config
  const [ loaded, setLoaded ] = useState(false)
  const [ failed, setFailed ] = useState(false)
  const [ company, setCompany ] = useState({
    companyId: null,
    companyName: null,
    enteredText: null,
  })
  // Nodes, not booleans: v7 panels remount the container on every return.
  const initializedNode = useRef(null)
  const initializedResultsNode = useRef(null)
  // Reset per container, so a remount's empty echo isn't read as a clear.
  const typedInField = useRef(false)
  // Chained so a correction can't land before the pick it replaces.
  const pendingSave = useRef(Promise.resolve())

  useEffect(() => {
    if (!enabled) return

    // The plugin reads its public key from this global at load time
    window.DDCONF = { API_KEY: config.publicKey }

    const link = document.createElement('link')
    link.rel = 'stylesheet'
    link.href = config.cssUrl
    document.head.appendChild(link)

    const script = document.createElement('script')
    script.src = config.scriptUrl
    script.async = true
    script.id = 'dtd-plugin'
    script.onload = () => setLoaded(true)
    script.onerror = () => setFailed(true)
    document.body.appendChild(script)

    return () => {
      // .remove() is a no-op if the plugin already moved/detached the node.
      script.remove()
      link.remove()
      delete window.DDCONF
    }
  }, [])

  const init = () => {
    const node = document.getElementById(CONTAINER_ID)
    if (!node || initializedNode.current === node) return
    const dtd = window.doublethedonation
    if (!dtd?.plugin) return

    initializedNode.current = node
    typedInField.current = false
    try {
      if (dtd.plugin.v2?.load_streamlined_search) {
        dtd.plugin.v2.load_streamlined_search(
          node,
          config.publicKey,
          {
            on_company_changed: selected => setCompany(company => selected
              ? {
                ...company,
                companyId: selected.id ?? selected.company_id ?? null,
                companyName: selected.name ?? selected.company_name ?? null,
              }
              // A de-select drops the text too, unless it's already a new search.
              : {
                companyId: null,
                companyName: null,
                enteredText: company.enteredText &&
                  !stillMatches(company.companyName, company.enteredText)
                  ? company.enteredText
                  : null,
              }),
            on_text_changed: text => {
              const typed = !!text?.trim()
              // Empty before any typing is a remount's echo (v7 Back), not a clear.
              const cleared = !typed && typedInField.current
              if (typed) typedInField.current = true

              setCompany(company => {
                if (!typed) return cleared ? { ...company, enteredText: null } : company

                // Drop the company once the text names another, so id and text can't disagree.
                return {
                  ...(stillMatches(company.companyName, text)
                    ? company
                    : { companyId: null, companyName: null }),
                  enteredText: text,
                }
              })
            },
          }
        )
      } else if (dtd.plugin.load_streamlined_input) {
        // Safe without v2: the auto-init renders the same legacy widget.
        node.id = DTD_LEGACY_ID
        dtd.plugin.load_streamlined_input()
      }
    } catch (error) {
      console.error('Double the Donation plugin failed to initialize', error)
      setFailed(true)
    }
  }

  // The v1 plugin keeps hidden inputs instead of firing callbacks.
  const hiddenInput = name =>
    document.querySelector(`input[name="${name}"]`)?.value || null

  const captured = () => ({
    companyId: company.companyId || hiddenInput('doublethedonation_company_id'),
    companyName: company.companyName || hiddenInput('doublethedonation_company_name'),
    enteredText: company.enteredText || hiddenInput('doublethedonation_entered_text'),
  })

  // `{}` when the search never ran: a row must exist iff the donor was asked.
  const orderProps = () => {
    if (!enabled || failed) return {}

    const { companyId, companyName, enteredText } = captured()
    return {
      employer_match_attributes: {
        company_id: companyId,
        company_name: companyName,
        entered_text: enteredText,
      },
    }
  }

  // Selections only, not text: registration is first-wins, and keystrokes would
  // register a fragment. A de-select saves nulls, so a pending registration skips it.
  const saveSelection = (saveUrl, selected) => {
    const save = () => fetch(saveUrl, {
      method: 'PATCH',
      headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
      body: JSON.stringify({ order: { employer_match_attributes: {
        company_id: selected?.id ?? selected?.company_id ?? null,
        company_name: selected?.name ?? selected?.company_name ?? null,
      } } }),
    }).then(response => {
      if (!response.ok) console.error('Failed to save employer selection', response.status)
    })

    pendingSave.current = pendingSave.current.then(save)
      .catch(error => console.error('Failed to save employer selection', error))
  }

  // With no company it shows a blank search. `saveUrl` is the donation's PATCH
  // endpoint, for a pick made here by a donor who skipped the search earlier.
  const loadResults = (donationIdentifier, saveUrl) => {
    if (!enabled || failed) return

    const node = document.getElementById(RESULTS_CONTAINER_ID)
    if (!node || initializedResultsNode.current === node) return

    // No v1 fallback: it needs DTD's own container id (see CONTAINER_ID).
    const load = window.doublethedonation?.plugin?.v2?.load_plugin
    if (!load) return

    initializedResultsNode.current = node
    try {
      const { companyId, companyName, enteredText } = captured()
      const engaged = !!(companyId || enteredText)
      load(node, config.publicKey, {
        // Only donors engaged at submit; a pick here is registered via saveUrl instead.
        donation_identifier: engaged ? donationIdentifier || null : null,
        dtd_company_id: companyId,
        // Their "entered text" field, so a blank search opens on what was typed.
        company_name: companyName || enteredText,
        // Not `isolated`: its shadow root (`all: initial`) would block our
        // .employer-matching-results restyling.
        ...(!engaged && saveUrl && {
          on_company_changed: selected => saveSelection(saveUrl, selected),
        }),
      })
    } catch (error) {
      console.error('Double the Donation results plugin failed to initialize', error)
      setFailed(true)
    }
  }

  // employerEntered skips the v1 hidden inputs, which wouldn't keep it live anyway.
  return {
    enabled, loaded, failed, init, loadResults, orderProps,
    companyName: company.companyName,
    employerEntered: !!(company.companyId || company.enteredText),
  }
}

export default useEmployerMatching
